<template>
    <div class="relative isolate bg-white pb-20 pt-16 sm:pt-20 border-b-4 border-accent">
        <div class="absolute inset-x-0 top-1/2 -z-10 -translate-y-1/2 transform-gpu overflow-hidden opacity-30 blur-3xl" aria-hidden="true">
            <div
                class="ml-[max(50%,38rem)] aspect-[1313/771] w-[82.0625rem] bg-gradient-to-tr from-secondary-accent to-white"
                 style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)" />
        </div>
        <div
            class="absolute inset-x-0 top-0 -z-10 flex transform-gpu overflow-hidden pt-16 opacity-25 blur-3xl sm:pt-20 xl:justify-end" aria-hidden="true">
            <div
                class="ml-[-22rem] aspect-[1313/771] w-[82.0625rem] flex-none origin-top-right rotate-[30deg] bg-gradient-to-tr from-secondary-accent to-white xl:ml-0 xl:mr-[calc(50%-12rem)]" style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)" />
        </div>
        <div
            class="mx-auto max-w-76 mobile-lg:max-w-xs mobile-xl:max-w-lg tablet-sm:max-w-2xl tablet-lg:max-w-4xl laptop-lg:max-w-6xl desktop:max-w-7xl px-2 lg:px-4">
            <div class="mx-auto max-w-3xl text-center">
                <h2 class="text-2xl/7 font-semibold text-secondary-accent">Testimonials</h2>
                <p class="mt-2 text-balance text-4xl font-semibold tracking-tight sm:text-5xl"
                >Trusted by Buyers & Sellers Across Northeast Florida</p>
                <p class="mt-2 text-balance tracking-tight text-base tablet-sm:text-lg border-b-2 border-accent pb-2"
                >From first-time buyers to retirees relocating to 55+ communities, here’s what clients say about working with me:</p>
            </div>
            <div
                class="mx-auto mt-12 grid max-w-2xl grid-cols-1 grid-rows-1 gap-8
                       text-sm/6 text-primary-text sm:mt-16 sm:grid-cols-2 xl:mx-0 xl:max-w-none xl:grid-flow-col xl:grid-cols-4">
                <figure
                    class="rounded-sm bg-white shadow-lg ring-2 ring-secondary-bg sm:col-span-2 xl:col-start-2 xl:row-end-1">
                    <blockquote class="p-6 text-lg font-semibold tracking-tight text-primary-text sm:p-12 sm:text-xl/8">
                        <p>{{ `“${featuredTestimonial.body}”` }}</p>
                    </blockquote>
                    <figcaption
                        class="flex flex-wrap items-center gap-x-4 gap-y-4 border-t border-secondary-accent px-6 py-4 sm:flex-nowrap">
                        <div class="flex-auto">
                            <div class="text-lg">{{ featuredTestimonial.author.name }}</div>
                        </div>
                    </figcaption>
                </figure>
                <div v-for="(columnGroup, columnGroupIdx) in testimonials" :key="columnGroupIdx" class="space-y-8 xl:contents xl:space-y-0">
                    <div v-for="(column, columnIdx) in columnGroup" :key="columnIdx" :class="[(columnGroupIdx === 0 && columnIdx === 0) || (columnGroupIdx === testimonials.length - 1 && columnIdx === columnGroup.length - 1) ? 'xl:row-span-2' : 'xl:row-start-1', 'space-y-8']">
                        <figure v-for="testimonial in column" :key="testimonial.author.handle"
                                class="rounded-sm bg-white p-6 shadow-lg ring-2 ring-secondary-bg text-lg">
                            <blockquote class="text-gray-900">
                                <p>{{ `“${testimonial.body}”` }}</p>
                            </blockquote>
                            <figcaption class="mt-6 flex items-center gap-x-4">
                                <div>
                                    <div class="text-lg">{{ testimonial.author.name }}</div>
                                </div>
                            </figcaption>
                        </figure>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
const allTestimonials = [
    {
        body: 'We couldn’t have been happier with the selling of our home. Gina was excellent in every step of the process! Even with extenuating circumstances.',
        author: {
            name: 'K & N Cook.',
        },
    },
    {
        body: 'Thank you so much for making this whole process run so smoothly. You truly make the home buying experience enjoyable.',
        author: {
            name: 'A. Stricklin',
        },
    },
    {
        body: 'Gina was friendly, patient and knowledgeable and she gave us ALL her time.',
        author: {
            name: 'L. & W. Feeks',
        },
    },
    {
        body: 'Gina went above and beyond for me and I truly appreciate all her hard work. She was always available and returned my calls right away. Couldn’t have done it without her.',
        author: {
            name: 'G. Romero',
        },
    },
    {
        body: 'Gina was extremely professional and kept us very informed during the entire selling process. Gina helped make this sale the easiest we have ever been through.',
        author: {
            name: 'G. & K. Thomas',
        },
    },
    {
        body: 'Gina Betbeze was great and I would highly recommend her to anyone selling or buying a home.',
        author: {
            name: 'D. & H. Pratt',
        },
    },
    {
        body: 'Gina Betbeze was very professional and responsive to our requests. She was very helpful, prepared and knowledgeable.',
        author: {
            name: 'D. Billings',
        },
    },
];

// Randomly select a featured testimonial
const randomIndex = Math.floor(Math.random() * allTestimonials.length);
const featuredTestimonial = allTestimonials[randomIndex];

// Remove the featured testimonial from the list
const remainingTestimonials = allTestimonials.filter((_, index) => index !== randomIndex);

// Organize remaining testimonials into a structure for display (4 columns)
const testimonials = [
    [remainingTestimonials.slice(0, 2)], // First column
    [remainingTestimonials.slice(2, 3)], // Second column
    [remainingTestimonials.slice(3, 4)], // Third column
    [remainingTestimonials.slice(4, 6)], // Fourth column
];

</script>
